import {
    EDimOrientation,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    ETextRole,
    IDimPrimitive,
    IPlanPrimitive,
    IProjectionOutput,
    ITextPrimitive,
} from './plan-primitive';
import { IPxBounds, boxesOverlap, projectionBounds, textBox } from './projection-bounds';
import { ITextMeasurer } from './text-measurer';

/** Clearance kept between two labels and between a label and the pane edge, pixels. */
const LABEL_CLEARANCE: number = 2;
/** How many places past each end a dimension label tries before it stays where it was. */
const DIM_TRIES: number = 4;
/** How many rings of places around itself a text tries before it stays where it was. */
const TEXT_RINGS: number = 3;

function shiftBox(box: IPxBounds, dx: number, dy: number): IPxBounds {
    return { minX: box.minX + dx, minY: box.minY + dy, maxX: box.maxX + dx, maxY: box.maxY + dy };
}

function inside(box: IPxBounds, bounds: IPxBounds | null): boolean {
    if (bounds === null) {
        return true;
    }
    const fitsX: boolean = box.minX >= bounds.minX && box.maxX <= bounds.maxX;
    return fitsX && box.minY >= bounds.minY && box.maxY <= bounds.maxY;
}

function free(box: IPxBounds, placed: readonly IPxBounds[], bounds: IPxBounds | null): boolean {
    const padded: IPxBounds = {
        minX: box.minX - LABEL_CLEARANCE,
        minY: box.minY - LABEL_CLEARANCE,
        maxX: box.maxX + LABEL_CLEARANCE,
        maxY: box.maxY + LABEL_CLEARANCE,
    };
    return inside(box, bounds) && placed.every((other: IPxBounds) => !boxesOverlap(padded, other, 0));
}

function dimBox(prim: IDimPrimitive): IPxBounds {
    return { minX: prim.labelBox[0], minY: prim.labelBox[1], maxX: prim.labelBox[2], maxY: prim.labelBox[3] };
}

/**
 * Where a dimension label goes: in the middle of its span when nothing is there, otherwise
 * past one end of the span — the far end first, then the near one, each a label-length further
 * on every try — as a drawing places the figure of a dimension too short for it.
 */
function placeDim(prim: IDimPrimitive, placed: IPxBounds[], bounds: IPxBounds | null): IDimPrimitive {
    const box: IPxBounds = dimBox(prim);
    const horizontal: boolean = prim.orientation === EDimOrientation.Horizontal;
    const span: number = horizontal ? Math.abs(prim.to[0] - prim.from[0]) : Math.abs(prim.to[1] - prim.from[1]);
    const length: number = horizontal ? box.maxX - box.minX : box.maxY - box.minY;
    const out: number = span / 2 + length / 2 + prim.tick + LABEL_CLEARANCE;
    const shifts: number[] = [0];
    for (let step: number = 0; step < DIM_TRIES; step++) {
        shifts.push(out + step * (length + LABEL_CLEARANCE), -(out + step * (length + LABEL_CLEARANCE)));
    }
    for (const shift of shifts) {
        const moved: IPxBounds = horizontal ? shiftBox(box, shift, 0) : shiftBox(box, 0, shift);
        if (free(moved, placed, bounds)) {
            placed.push(moved);
            return shift === 0
                ? prim
                : { ...prim, labelShift: (prim.labelShift ?? 0) + shift, labelBox: [moved.minX, moved.minY, moved.maxX, moved.maxY] };
        }
    }
    placed.push(box);
    return prim;
}

/**
 * Where a text goes: where it stands when nothing is there, otherwise the nearest free place
 * around it; an optional text has no other place and is left out (null).
 */
function placeText(prim: ITextPrimitive, measurer: ITextMeasurer, placed: IPxBounds[], bounds: IPxBounds | null): ITextPrimitive | null {
    const box: IPxBounds = textBox(prim, measurer);
    const stepX: number = box.maxX - box.minX + LABEL_CLEARANCE;
    const stepY: number = box.maxY - box.minY + LABEL_CLEARANCE;
    const offsets: [number, number][] = [[0, 0]];
    for (let ring: number = 1; ring <= (prim.optional ? 0 : TEXT_RINGS); ring++) {
        offsets.push([0, -ring * stepY], [0, ring * stepY], [ring * stepX, 0], [-ring * stepX, 0]);
    }
    for (const [dx, dy] of offsets) {
        const moved: IPxBounds = shiftBox(box, dx, dy);
        if (free(moved, placed, bounds)) {
            placed.push(moved);
            return dx === 0 && dy === 0 ? prim : { ...prim, at: [prim.at[0] + dx, prim.at[1] + dy] };
        }
    }
    if (prim.optional) {
        return null;
    }
    placed.push(box);
    return prim;
}

/**
 * The projection with its labels kept apart: every text and dimension label, in drawing order,
 * takes its own place if it is free and the nearest free place otherwise — a dimension label
 * past an end of its span, a text a step aside, an optional text (a step number) left out.
 * `bounds` keeps a moved label inside the pane;
 * null lets it go anywhere (the pane is then cut to the drawing afterwards).
 */
export function declutterLabels(output: IProjectionOutput, measurer: ITextMeasurer, bounds: IPxBounds | null): IProjectionOutput {
    const placed: IPxBounds[] = [];
    const primitives: IPlanPrimitive[] = [];
    for (const prim of output.primitives) {
        if (prim.kind === EPrimitiveKind.Dim) {
            primitives.push(placeDim(prim, placed, bounds));
        } else if (prim.kind === EPrimitiveKind.Text) {
            const placedText: ITextPrimitive | null = placeText(prim, measurer, placed, bounds);
            if (placedText) {
                primitives.push(placedText);
            }
        } else {
            primitives.push(prim);
        }
    }
    return { ...output, primitives };
}

/** Gap between the drawing and the notes above it, and between a level label and the drawing, pixels. */
const NOTE_GAP: number = 6;
/** Gap between two rows of notes, pixels. */
const NOTE_ROW_GAP: number = 4;

function hasRole(prim: IPlanPrimitive, role: ETextRole): prim is ITextPrimitive {
    return prim.kind === EPrimitiveKind.Text && prim.role === role;
}

/**
 * The drawing's own box: everything but its notes and level labels, dimensions included.
 * Null when the view draws nothing.
 */
export function drawingBounds(output: IProjectionOutput, measurer: ITextMeasurer): IPxBounds | null {
    const drawing: IPlanPrimitive[] = output.primitives.filter(
        (prim: IPlanPrimitive) => !hasRole(prim, ETextRole.Note) && !hasRole(prim, ETextRole.Level)
    );
    return projectionBounds({ ...output, primitives: drawing }, measurer);
}

/**
 * The view's notes and level labels set out of its drawing, as a sheet does: the notes in rows
 * above the drawing's box, flush with its left edge, in their own top-to-bottom order; each level
 * label beside the box on the right, at its own level.
 */
export function placeNotes(output: IProjectionOutput, measurer: ITextMeasurer): IProjectionOutput {
    const box: IPxBounds | null = drawingBounds(output, measurer);
    if (!box) {
        return output;
    }
    const moved: Map<IPlanPrimitive, ITextPrimitive> = new Map<IPlanPrimitive, ITextPrimitive>();
    const notes: ITextPrimitive[] = output.primitives
        .filter((prim: IPlanPrimitive): prim is ITextPrimitive => hasRole(prim, ETextRole.Note))
        .sort((a: ITextPrimitive, b: ITextPrimitive) => b.at[1] - a.at[1]);
    let bottom: number = box.minY - NOTE_GAP;
    for (const noteText of notes) {
        const top: number = bottom - noteText.font.size;
        moved.set(noteText, { ...noteText, at: [box.minX, top], align: ETextAlign.Left, baseline: ETextBaseline.Top, rotation: 0 });
        bottom = top - NOTE_ROW_GAP;
    }
    for (const prim of output.primitives) {
        if (hasRole(prim, ETextRole.Level)) {
            moved.set(prim, { ...prim, at: [box.maxX + NOTE_GAP, prim.at[1]], align: ETextAlign.Left });
        }
    }
    return { ...output, primitives: output.primitives.map((prim: IPlanPrimitive) => moved.get(prim) ?? prim) };
}
